import { useState, useCallback, useEffect, useRef } from 'react';
import { ArrowGoIcon, DeleteIcon } from '@/icons';
import styles from '@/components/jog/numpad.module.css';

type Field = 'step' | 'feed';

interface StepFeedNumpadProps {
  /** Current Step Size (mm) — shown when the step field hasn't been typed into. */
  stepValue: number;
  /** Current Feed Rate (mm/min) — shown when the feed field hasn't been typed into. */
  feedValue: number;
  /** Which field is focused when the sheet opens. */
  initialField: Field;
  /** Fires on Set with the raw numpad strings (decimal point); '' = untouched. */
  onSubmit: (step: string, feed: string) => void;
  onClose: () => void;
}

type KeyId =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '-'
  | ','
  | 'delete'
  | 'set';

/** Combined Step Size + Feed Rate numeric entry sheet (see design step.png). */
export function StepFeedNumpad({
  stepValue,
  feedValue,
  initialField,
  onSubmit,
  onClose,
}: StepFeedNumpadProps): JSX.Element {
  // Typed values per field (empty = keep the current store value).
  const [stepInput, setStepInput] = useState('');
  const [feedInput, setFeedInput] = useState('');
  const [active, setActive] = useState<Field>(initialField);
  // Which key is visually highlighted (cleared after 120ms).
  const [activeKey, setActiveKey] = useState<KeyId | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const editActive = useCallback(
    (fn: (prev: string) => string) => {
      if (active === 'step') setStepInput(fn);
      else setFeedInput(fn);
    },
    [active],
  );

  /** Flash the active key indicator for 120ms. */
  const flashKey = useCallback((key: KeyId) => {
    setActiveKey(key);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setActiveKey(null), 120);
  }, []);

  // Clear any pending flash timer on unmount.
  useEffect(
    () => () => {
      if (flashTimer.current) clearTimeout(flashTimer.current);
    },
    [],
  );

  const handleDigit = useCallback(
    (digit: string, key: KeyId) => {
      flashKey(key);
      editActive((prev) => prev + digit);
    },
    [flashKey, editActive],
  );

  const handleDelete = useCallback(() => {
    flashKey('delete');
    editActive((prev) => prev.slice(0, -1));
  }, [flashKey, editActive]);

  const handleSet = useCallback(() => {
    flashKey('set');
    onSubmit(stepInput, feedInput);
    onClose();
  }, [stepInput, feedInput, onSubmit, onClose, flashKey]);

  // Physical keyboard support.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9') handleDigit(e.key, e.key as KeyId);
      else if (e.key === '-') handleDigit('-', '-');
      else if (e.key === ',' || e.key === '.') handleDigit('.', ',');
      else if (e.key === 'Backspace') handleDelete();
      else if (e.key === 'Enter') handleSet();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleDigit, handleDelete, handleSet, onClose]);

  const keyClass = (key: KeyId, extra?: string) =>
    [styles.numpadKey, extra ?? '', activeKey === key ? styles.numpadKeyActive : '']
      .filter(Boolean)
      .join(' ');

  const fieldClass = (field: Field) =>
    [styles.numpadField, active === field ? styles.numpadFieldActive : '']
      .filter(Boolean)
      .join(' ');

  return (
    <div
      className={styles.numpadOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Step Size and Feed Rate input"
    >
      <div className={styles.numpadSheet} onClick={(e) => e.stopPropagation()}>
        {/* Value fields — tap to switch which one the keys edit */}
        <div className={styles.numpadFields}>
          <button
            className={fieldClass('step')}
            type="button"
            onClick={() => setActive('step')}
            aria-label="Edit Step Size"
            aria-pressed={active === 'step'}
          >
            <span className={styles.numpadFieldLabel}>Step Size</span>
            <span className={styles.numpadFieldRow}>
              <span className={styles.numpadFieldValue}>{stepInput || String(stepValue)}</span>
              <span className={styles.numpadFieldUnit}>mm</span>
            </span>
          </button>
          <button
            className={fieldClass('feed')}
            type="button"
            onClick={() => setActive('feed')}
            aria-label="Edit Feed Rate"
            aria-pressed={active === 'feed'}
          >
            <span className={styles.numpadFieldLabel}>Feed Rate</span>
            <span className={styles.numpadFieldRow}>
              <span className={styles.numpadFieldValue}>{feedInput || String(feedValue)}</span>
              <span className={styles.numpadFieldUnit}>mm/min</span>
            </span>
          </button>
        </div>

        {/* Key grid: 4 rows × 4 cols (3 digit cols + 1 action col) */}
        <div className={styles.numpadGrid}>
          {/* Row 1 */}
          <button className={keyClass('7')} type="button" onClick={() => handleDigit('7', '7')}>
            7
          </button>
          <button className={keyClass('8')} type="button" onClick={() => handleDigit('8', '8')}>
            8
          </button>
          <button className={keyClass('9')} type="button" onClick={() => handleDigit('9', '9')}>
            9
          </button>
          <button
            className={keyClass('delete', styles.numpadKeyDelete)}
            type="button"
            aria-label="Delete last character"
            onClick={handleDelete}
          >
            <DeleteIcon width={28} height={22} />
          </button>

          {/* Row 2 */}
          <button className={keyClass('4')} type="button" onClick={() => handleDigit('4', '4')}>
            4
          </button>
          <button className={keyClass('5')} type="button" onClick={() => handleDigit('5', '5')}>
            5
          </button>
          <button className={keyClass('6')} type="button" onClick={() => handleDigit('6', '6')}>
            6
          </button>
          {/* Set spans rows 2–4 */}
          <button
            className={keyClass('set', styles.numpadKeySet)}
            type="button"
            aria-label="Apply values"
            onClick={handleSet}
          >
            <span className={styles.numpadKeySetLabel}>
              Set <ArrowGoIcon width={24} height={22} />
            </span>
          </button>

          {/* Row 3 */}
          <button className={keyClass('1')} type="button" onClick={() => handleDigit('1', '1')}>
            1
          </button>
          <button className={keyClass('2')} type="button" onClick={() => handleDigit('2', '2')}>
            2
          </button>
          <button className={keyClass('3')} type="button" onClick={() => handleDigit('3', '3')}>
            3
          </button>
          {/* 4th cell in row 3 is occupied by Set (span 3) — no element needed */}

          {/* Row 4 */}
          <button className={keyClass('-')} type="button" onClick={() => handleDigit('-', '-')}>
            −
          </button>
          <button className={keyClass('0')} type="button" onClick={() => handleDigit('0', '0')}>
            0
          </button>
          <button className={keyClass(',')} type="button" onClick={() => handleDigit('.', ',')}>
            .
          </button>
          {/* 4th cell in row 4 is occupied by Set (span 3) — no element needed */}
        </div>

        {/* iPhone-style home indicator */}
        <div className={styles.numpadHomeBar} />
      </div>
    </div>
  );
}
